import {
  bucketLabel,
  type DashboardRange,
  type DashboardVisitBucket,
  niceAxis,
  X_LABEL_EVERY,
} from '@c214/shared';

const WIDTH = 800;
const HEIGHT = 240;
const PAD = { top: 12, right: 12, bottom: 28, left: 44 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

// Власний SVG замість бібліотеки графіків: дві лінії й сітка — це кілька десятків
// рядків, а фіксований viewBox масштабує все разом з текстом без перерахунку.
export function VisitsChart({
  buckets,
  range,
}: {
  buckets: DashboardVisitBucket[];
  range: DashboardRange;
}) {
  const axis = niceAxis(Math.max(0, ...buckets.map((b) => Math.max(b.visitors, b.pageviews))));
  const x = (i: number) =>
    PAD.left + (buckets.length > 1 ? (i * PLOT_W) / (buckets.length - 1) : 0);
  const y = (value: number) => PAD.top + PLOT_H * (1 - value / axis.max);
  const line = (pick: (b: DashboardVisitBucket) => number) =>
    buckets.map((b, i) => `${x(i)},${y(pick(b))}`).join(' ');
  const visitorsLine = line((b) => b.visitors);
  const area = `${PAD.left},${y(0)} ${visitorsLine} ${x(buckets.length - 1)},${y(0)}`;
  const every = X_LABEL_EVERY[range];
  // 24h: підписуємо кожну шосту годину доби (00, 06, 12, 18), а не кожен шостий кошик.
  const showLabel = (b: DashboardVisitBucket, i: number) =>
    range === '24h' ? Number(b.start.slice(11, 13)) % 6 === 0 : i % every === 0;
  // Крайні підписи притискаємо всередину, щоб вони не виходили за viewBox.
  const labelAnchor = (i: number) =>
    i === buckets.length - 1 ? 'end' : i === 0 ? 'start' : 'middle';

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="block h-auto w-full"
      role="img"
      aria-label="Графік відвідувань"
    >
      {axis.ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={y(tick)}
            y2={y(tick)}
            className="stroke-line"
            strokeWidth={1}
          />
          <text
            x={PAD.left - 8}
            y={y(tick) + 4}
            textAnchor="end"
            className="fill-muted-2 font-mono text-[11px]"
          >
            {tick}
          </text>
        </g>
      ))}
      {buckets.map((b, i) =>
        showLabel(b, i) ? (
          <text
            key={b.start}
            x={x(i)}
            y={HEIGHT - 8}
            textAnchor={labelAnchor(i)}
            className="fill-muted-2 font-mono text-[11px]"
          >
            {bucketLabel(b.start)}
          </text>
        ) : null,
      )}
      <polygon points={area} className="fill-accent/10" />
      <polyline
        points={line((b) => b.pageviews)}
        fill="none"
        className="stroke-muted-2"
        strokeWidth={1.5}
      />
      <polyline points={visitorsLine} fill="none" className="stroke-accent" strokeWidth={2} />
    </svg>
  );
}
